/* ==========================================================================
   ANRIXAUTO — Theme JavaScript
   Vanilla JS, no dependencies. Loaded with `defer`.
   Uses Shopify's native Cart AJAX API + Section Rendering API.
   ========================================================================== */
(function () {
  'use strict';

  var PREFERS_REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var routes = window.routes || {};
  var strings = window.themeStrings || {};

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  /* ------------------------------------------------------------------ *
   * Toasts
   * ------------------------------------------------------------------ */
  var Toast = {
    stack: null,
    ensure: function () {
      if (!this.stack) {
        this.stack = document.createElement('div');
        this.stack.className = 'toast-stack';
        this.stack.setAttribute('role', 'status');
        this.stack.setAttribute('aria-live', 'polite');
        document.body.appendChild(this.stack);
      }
      return this.stack;
    },
    show: function (message, type) {
      var stack = this.ensure();
      var el = document.createElement('div');
      el.className = 'toast' + (type === 'error' ? ' toast--error' : '');
      var icon = type === 'error'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v6M12 16.5v.01"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m4.5 12.5 4.5 4.5 10-10"/></svg>';
      el.innerHTML = icon + '<span></span>';
      el.querySelector('span').textContent = message;
      stack.appendChild(el);
      requestAnimationFrame(function () { el.classList.add('is-visible'); });
      setTimeout(function () {
        el.classList.remove('is-visible');
        setTimeout(function () { el.remove(); }, 400);
      }, 3800);
    }
  };

  /* ------------------------------------------------------------------ *
   * Overlay controller (drawers, modals, search)
   * ------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';
  var openStack = [];

  function trapFocus(container) {
    function handler(e) {
      if (e.key !== 'Tab') return;
      var items = $$(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.__trap = handler;
    container.addEventListener('keydown', handler);
  }
  function releaseFocus(container) {
    if (container.__trap) container.removeEventListener('keydown', container.__trap);
  }

  function openOverlay(el, opts) {
    if (!el || el.classList.contains('is-open')) return;
    opts = opts || {};
    el.__opener = document.activeElement;
    el.classList.add('is-open');
    el.removeAttribute('aria-hidden');
    document.body.classList.add('drawer-open');
    openStack.push(el);
    trapFocus(el);
    var focusTarget = opts.focus || el.querySelector('[data-autofocus]') || el.querySelector(FOCUSABLE);
    if (focusTarget) setTimeout(function () { focusTarget.focus({ preventScroll: true }); }, 120);
    el.dispatchEvent(new CustomEvent('overlay:open'));
  }

  function closeOverlay(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    releaseFocus(el);
    openStack = openStack.filter(function (o) { return o !== el; });
    if (!openStack.length) document.body.classList.remove('drawer-open');
    if (el.__opener && typeof el.__opener.focus === 'function') el.__opener.focus({ preventScroll: true });
    el.dispatchEvent(new CustomEvent('overlay:close'));
  }
  window.closeOverlay = closeOverlay;
  window.openOverlay = openOverlay;

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-open'));
      if (target) { e.preventDefault(); openOverlay(target); }
      return;
    }
    var closer = e.target.closest('[data-close]');
    if (closer) {
      var overlay = closer.closest('.drawer, .modal, .search-modal');
      if (overlay) { e.preventDefault(); closeOverlay(overlay); }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openStack.length) {
      closeOverlay(openStack[openStack.length - 1]);
    }
  });

  /* ------------------------------------------------------------------ *
   * Sticky header
   * ------------------------------------------------------------------ */
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;
    var lastY = 0, ticking = false;
    function update() {
      var y = window.scrollY || window.pageYOffset;
      header.classList.toggle('is-scrolled', y > 12);
      lastY = y;
      ticking = false;
    }
    on(window, 'scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();

    // Publish header height for sticky offsets
    var lastH = 0;
    function setHeight() {
      // Writes to --header-height only, which nothing inside the header
      // consumes. --header-min-height is what sizes the bar itself, so this
      // can never feed back into its own measurement.
      var h = Math.round(header.getBoundingClientRect().height);
      if (h === lastH || h === 0) return;
      lastH = h;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    setHeight();
    if (window.ResizeObserver) new ResizeObserver(setHeight).observe(header);
    else on(window, 'resize', debounce(setHeight, 200));
  }

  /* ------------------------------------------------------------------ *
   * Desktop navigation dropdowns
   * ------------------------------------------------------------------ */
  function initNav() {
    var items = $$('[data-nav-item]');
    if (!items.length) return;
    var closeTimer;

    items.forEach(function (item) {
      var trigger = $('[data-nav-trigger]', item);
      var panel = $('[data-nav-panel]', item);
      if (!trigger || !panel) return;

      function open() {
        clearTimeout(closeTimer);
        items.forEach(function (i) { if (i !== item) close(i); });
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
      function close(target) {
        var t = target || item;
        t.classList.remove('is-open');
        var trg = $('[data-nav-trigger]', t);
        if (trg) trg.setAttribute('aria-expanded', 'false');
      }

      on(item, 'mouseenter', open);
      on(item, 'mouseleave', function () {
        closeTimer = setTimeout(function () { close(); }, 140);
      });
      on(trigger, 'click', function (e) {
        // Allow the parent link to navigate on second click; first click opens.
        if (!item.classList.contains('is-open')) { e.preventDefault(); open(); }
      });
      on(trigger, 'keydown', function (e) {
        if (e.key === 'ArrowDown') {
          e.preventDefault(); open();
          var first = panel.querySelector('a');
          if (first) first.focus();
        }
      });
      on(item, 'focusout', function (e) {
        if (!item.contains(e.relatedTarget)) close();
      });
      on(panel, 'keydown', function (e) {
        if (e.key === 'Escape') { close(); trigger.focus(); }
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Predictive search
   * ------------------------------------------------------------------ */
  function initSearch() {
    var modal = $('[data-search-modal]');
    if (!modal) return;
    var input = $('[data-search-input]', modal);
    var results = $('[data-search-results]', modal);
    var defaultContent = $('[data-search-default]', modal);
    var controller = null;
    if (!input || !results) return;

    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        if (defaultContent) defaultContent.hidden = false;
        return;
      }
      if (defaultContent) defaultContent.hidden = true;
      if (controller) controller.abort();
      controller = new AbortController();
      var url = (routes.predictive_search_url || '/search/suggest') +
        '?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page,article' +
        '&resources[limit]=6' +
        '&section_id=predictive-search';
      results.setAttribute('aria-busy', 'true');
      fetch(url, { signal: controller.signal })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var inner = doc.querySelector('#shopify-section-predictive-search') || doc.body;
          results.innerHTML = inner.innerHTML;
          results.removeAttribute('aria-busy');
        })
        .catch(function (err) {
          if (err.name !== 'AbortError') results.removeAttribute('aria-busy');
        });
    }, 260);

    on(input, 'input', run);
    on(modal, 'overlay:close', function () {
      input.value = '';
      results.innerHTML = '';
      if (defaultContent) defaultContent.hidden = false;
    });

    // Keyboard shortcut: "/" focuses search
    on(document, 'keydown', function (e) {
      if (e.key === '/' && !/input|textarea|select/i.test(document.activeElement.tagName) && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        openOverlay(modal, { focus: input });
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart
   * ------------------------------------------------------------------ */
  var Cart = {
    sectionsToRender: function () {
      var ids = [];
      if ($('[data-cart-drawer]')) ids.push('cart-drawer');
      if ($('[data-cart-icon-bubble]')) ids.push('cart-icon-bubble');
      // The cart page lives in a JSON template, so its section id is dynamic
      var page = $('[data-cart-page]');
      if (page && page.getAttribute('data-cart-page')) ids.push(page.getAttribute('data-cart-page'));
      return ids;
    },

    updateSections: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        var html = sections[id];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('#shopify-section-' + id) || doc.querySelector('[data-section-' + id + ']') || doc.body;
        var target = document.getElementById('shopify-section-' + id);
        if (target && source) {
          target.innerHTML = source.innerHTML;
        } else if (id === 'cart-icon-bubble') {
          var bubble = $('[data-cart-icon-bubble]');
          if (bubble && source) bubble.innerHTML = source.innerHTML;
        }
      });
      this.bump();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },

    bump: function () {
      var count = $('[data-cart-count]');
      if (count) {
        count.classList.remove('is-bumped');
        void count.offsetWidth;
        count.classList.add('is-bumped');
      }
    },

    add: function (formData, button) {
      var self = this;
      var ids = this.sectionsToRender();
      formData.append('sections', ids.join(','));
      formData.append('sections_url', window.location.pathname);
      if (button) { button.classList.add('is-loading'); button.setAttribute('aria-disabled', 'true'); }

      return fetch((routes.cart_add_url || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          if (!res.ok) {
            Toast.show(res.data.description || res.data.message || strings.cartError || 'Could not add to cart', 'error');
            return null;
          }
          self.updateSections(res.data.sections);
          Toast.show(strings.addedToCart || 'Added to cart');
          var drawer = $('[data-cart-drawer]');
          if (drawer && document.body.dataset.cartType === 'drawer') openOverlay(drawer);
          return res.data;
        })
        .catch(function () { Toast.show(strings.cartError || 'Something went wrong. Please try again.', 'error'); })
        .finally(function () {
          if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-disabled'); }
        });
    },

    change: function (line, quantity, row) {
      var self = this;
      if (row) row.classList.add('is-loading');
      return fetch((routes.cart_change_url || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: this.sectionsToRender().join(','),
          sections_url: window.location.pathname
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) { Toast.show(data.message || 'Could not update cart', 'error'); return; }
          self.updateSections(data.sections);
        })
        .catch(function () { Toast.show(strings.cartError || 'Something went wrong.', 'error'); })
        .finally(function () { if (row) row.classList.remove('is-loading'); });
    }
  };
  window.AnrixCart = Cart;

  function initCart() {
    // Add to cart forms (product page, quick add)
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form.product-form');
      if (!form) return;
      e.preventDefault();
      var button = form.querySelector('[type="submit"]');
      Cart.add(new FormData(form), button);
    });

    // Quantity steppers
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-change]');
      if (!btn) return;
      var wrap = btn.closest('[data-qty]');
      var input = wrap && wrap.querySelector('input');
      if (!input) return;
      var step = parseInt(btn.getAttribute('data-qty-change'), 10);
      var min = parseInt(input.getAttribute('min') || '0', 10);
      var max = input.getAttribute('max') ? parseInt(input.getAttribute('max'), 10) : Infinity;
      var next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + step));
      if (next === parseInt(input.value, 10)) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Cart line quantity change / remove
    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      var line = input.getAttribute('data-line');
      Cart.change(line, parseInt(input.value, 10) || 0, input.closest('.cart-item'));
    });

    document.addEventListener('click', function (e) {
      var remove = e.target.closest('[data-cart-remove]');
      if (!remove) return;
      e.preventDefault();
      Cart.change(remove.getAttribute('data-line'), 0, remove.closest('.cart-item'));
    });

    // Cart note
    document.addEventListener('change', function (e) {
      var note = e.target.closest('[data-cart-note]');
      if (!note) return;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.value })
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Quick add
   * ------------------------------------------------------------------ */
  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      var variantId = btn.getAttribute('data-variant-id');
      if (variantId) {
        var fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', '1');
        Cart.add(fd, btn);
        return;
      }
      // Multi-variant: load the product form into the quick-add modal
      var url = btn.getAttribute('data-product-url');
      var modal = $('[data-quick-add-modal]');
      if (!url || !modal) return;
      var body = $('[data-quick-add-body]', modal);
      btn.classList.add('is-loading');
      fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=quick-add')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var content = doc.querySelector('[data-quick-add-content]');
          body.innerHTML = content ? content.innerHTML : '';
          openOverlay(modal);
          initVariantPickers(body);
          initGallery(body);
        })
        .catch(function () { window.location.href = url; })
        .finally(function () { btn.classList.remove('is-loading'); });
    });
  }

  /* ------------------------------------------------------------------ *
   * Variant picker
   * ------------------------------------------------------------------ */
  function initVariantPickers(ctx) {
    $$('[data-variant-picker]', ctx || document).forEach(function (picker) {
      if (picker.__init) return;
      picker.__init = true;

      var scope = picker.closest('[data-product-root]') || document;
      var dataEl = $('[data-variant-json]', scope);
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var priceEl = $('[data-price-wrap]', scope);
      var idInput = $('[data-variant-id-input]', scope);
      var submitBtn = $('[data-add-button]', scope);
      var submitText = submitBtn && submitBtn.querySelector('.btn__text');
      var skuEl = $('[data-variant-sku]', scope);
      var stockEl = $('[data-variant-stock]', scope);
      var updateUrl = picker.getAttribute('data-update-url') === 'true';

      function currentOptions() {
        return $$('input:checked, select', picker).map(function (el) { return el.value; });
      }

      function findVariant(opts) {
        return variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === opts[i]; });
        });
      }

      function updateAvailability(opts) {
        // Disable option values that produce no purchasable combination
        $$('[data-option-position]', picker).forEach(function (group) {
          var pos = parseInt(group.getAttribute('data-option-position'), 10) - 1;
          $$('input', group).forEach(function (input) {
            var test = opts.slice();
            test[pos] = input.value;
            var match = variants.find(function (v) {
              return v.options.every(function (o, i) { return i === pos ? o === input.value : (test[i] === undefined || o === test[i]); });
            });
            var exists = variants.some(function (v) { return v.options[pos] === input.value; });
            var available = match && match.available;
            input.disabled = !exists;
            if (input.nextElementSibling) {
              input.nextElementSibling.style.opacity = available ? '' : '0.45';
            }
          });
        });
      }

      function update() {
        var opts = currentOptions();
        var variant = findVariant(opts);
        updateAvailability(opts);

        $$('[data-selected-value]', picker).forEach(function (el) {
          var pos = parseInt(el.getAttribute('data-selected-value'), 10) - 1;
          if (opts[pos]) el.textContent = opts[pos];
        });

        if (!variant) {
          if (submitBtn) { submitBtn.setAttribute('aria-disabled', 'true'); submitBtn.disabled = true; }
          if (submitText) submitText.textContent = strings.unavailable || 'Unavailable';
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (updateUrl && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        if (priceEl && variant.price_html) priceEl.innerHTML = variant.price_html;
        if (skuEl) { skuEl.textContent = variant.sku || ''; skuEl.closest('[data-sku-row]') && (skuEl.closest('[data-sku-row]').hidden = !variant.sku); }

        if (submitBtn) {
          submitBtn.disabled = !variant.available;
          submitBtn.setAttribute('aria-disabled', String(!variant.available));
          if (submitText) submitText.textContent = variant.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
        }

        if (stockEl) {
          stockEl.className = 'product__stock ' + (variant.available ? (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5 ? 'product__stock--low' : 'product__stock--in') : 'product__stock--out');
          var label = variant.available
            ? (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5
              ? (strings.lowStock || 'Low stock — only {{ count }} left').replace('{{ count }}', variant.inventory_quantity)
              : (strings.inStock || 'In stock, ready to ship'))
            : (strings.soldOut || 'Sold out');
          stockEl.innerHTML = '<span class="product__stock-dot"></span>' + label;
        }

        // Swap gallery to the variant's image
        if (variant.featured_media_id) {
          var slide = scope.querySelector('[data-media-id="' + variant.featured_media_id + '"]');
          if (slide) selectGallerySlide(slide.closest('[data-gallery]'), variant.featured_media_id);
        }
      }

      on(picker, 'change', update);
      update();
    });
  }

  /* ------------------------------------------------------------------ *
   * Product gallery
   * ------------------------------------------------------------------ */
  function selectGallerySlide(gallery, mediaId) {
    if (!gallery) return;
    $$('[data-media-id]', gallery).forEach(function (el) {
      var active = el.getAttribute('data-media-id') === String(mediaId);
      if (el.classList.contains('gallery__slide')) {
        el.classList.toggle('is-active', active);
      } else {
        el.classList.toggle('is-active', active);
        if (active) el.setAttribute('aria-current', 'true'); else el.removeAttribute('aria-current');
      }
    });
  }

  function initGallery(ctx) {
    $$('[data-gallery]', ctx || document).forEach(function (gallery) {
      if (gallery.__init) return;
      gallery.__init = true;

      on(gallery, 'click', function (e) {
        var thumb = e.target.closest('[data-gallery-thumb]');
        if (thumb) {
          e.preventDefault();
          selectGallerySlide(gallery, thumb.getAttribute('data-media-id'));
          return;
        }
        var arrow = e.target.closest('[data-gallery-arrow]');
        if (arrow) {
          e.preventDefault();
          var slides = $$('.gallery__slide', gallery);
          var idx = slides.findIndex(function (s) { return s.classList.contains('is-active'); });
          var next = (idx + parseInt(arrow.getAttribute('data-gallery-arrow'), 10) + slides.length) % slides.length;
          selectGallerySlide(gallery, slides[next].getAttribute('data-media-id'));
        }
      });

      // Swipe support
      var startX = null;
      on(gallery, 'touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      on(gallery, 'touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) < 46) return;
        var slides = $$('.gallery__slide', gallery);
        if (slides.length < 2) return;
        var idx = slides.findIndex(function (s) { return s.classList.contains('is-active'); });
        var next = (idx + (dx < 0 ? 1 : -1) + slides.length) % slides.length;
        selectGallerySlide(gallery, slides[next].getAttribute('data-media-id'));
      }, { passive: true });
    });
  }

  /* ------------------------------------------------------------------ *
   * Carousels
   * ------------------------------------------------------------------ */
  function initCarousels(ctx) {
    $$('[data-carousel]', ctx || document).forEach(function (carousel) {
      if (carousel.__init) return;
      carousel.__init = true;
      var viewport = $('[data-carousel-viewport]', carousel);
      var prev = $('[data-carousel-prev]', carousel);
      var next = $('[data-carousel-next]', carousel);
      if (!viewport) return;

      function step() {
        var slide = viewport.querySelector('.carousel__slide');
        return slide ? slide.getBoundingClientRect().width + 18 : viewport.clientWidth * 0.8;
      }
      function sync() {
        var maxScroll = viewport.scrollWidth - viewport.clientWidth - 2;
        if (prev) prev.disabled = viewport.scrollLeft <= 2;
        if (next) next.disabled = viewport.scrollLeft >= maxScroll;
        if (maxScroll <= 2) {
          if (prev) prev.disabled = true;
          if (next) next.disabled = true;
        }
      }
      on(prev, 'click', function () { viewport.scrollBy({ left: -step(), behavior: PREFERS_REDUCED ? 'auto' : 'smooth' }); });
      on(next, 'click', function () { viewport.scrollBy({ left: step(), behavior: PREFERS_REDUCED ? 'auto' : 'smooth' }); });
      on(viewport, 'scroll', debounce(sync, 90), { passive: true });
      on(window, 'resize', debounce(sync, 200));
      sync();
    });
  }

  /* ------------------------------------------------------------------ *
   * Scroll reveal
   * ------------------------------------------------------------------ */
  function initReveal() {
    var items = $$('.reveal:not(.is-visible)');
    if (!items.length) return;
    if (PREFERS_REDUCED || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------ *
   * Parallax + pointer depth (hero & showcase) — lightweight, rAF-driven
   * ------------------------------------------------------------------ */
  function initParallax() {
    if (PREFERS_REDUCED) return;

    var layers = $$('[data-parallax]');
    if (layers.length) {
      var ticking = false;
      function update() {
        var vh = window.innerHeight;
        layers.forEach(function (el) {
          var rect = el.getBoundingClientRect();
          if (rect.bottom < -200 || rect.top > vh + 200) return;
          var speed = parseFloat(el.getAttribute('data-parallax')) || 0.12;
          var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
          el.style.transform = 'translate3d(0,' + (progress * speed * 100).toFixed(2) + 'px,0) scale(' + (el.dataset.parallaxScale || 1.08) + ')';
        });
        ticking = false;
      }
      on(window, 'scroll', function () { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
      update();
    }

    // Pointer-follow depth for the 3D showcase
    $$('[data-depth-stage]').forEach(function (stage) {
      if (window.matchMedia('(hover: none)').matches) return;
      var car = $('[data-depth-car]', stage);
      var chips = $$('[data-depth-chip]', stage);
      var frame = null;

      on(stage, 'pointermove', function (e) {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          var rect = stage.getBoundingClientRect();
          var x = (e.clientX - rect.left) / rect.width - 0.5;
          var y = (e.clientY - rect.top) / rect.height - 0.5;
          if (car) {
            car.style.setProperty('--rx', (-y * 5).toFixed(2) + 'deg');
            car.style.setProperty('--ry', (x * 7).toFixed(2) + 'deg');
            car.style.setProperty('--px', (x * 12).toFixed(1) + 'px');
            car.style.setProperty('--py', (y * 8).toFixed(1) + 'px');
          }
          chips.forEach(function (chip, i) {
            var f = parseFloat(chip.getAttribute('data-depth-chip')) || (i + 1) * 8;
            chip.style.setProperty('--cx', (x * f).toFixed(1) + 'px');
            chip.style.setProperty('--cy', (y * f).toFixed(1) + 'px');
          });
        });
      });

      on(stage, 'pointerleave', function () {
        if (car) { car.style.removeProperty('--rx'); car.style.removeProperty('--ry'); car.style.removeProperty('--px'); car.style.removeProperty('--py'); }
        chips.forEach(function (chip) { chip.style.removeProperty('--cx'); chip.style.removeProperty('--cy'); });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Announcement rotator
   * ------------------------------------------------------------------ */
  function initAnnouncement() {
    $$('[data-announcement-rotate]').forEach(function (bar) {
      var items = $$('.announcement__item', bar);
      if (items.length < 2) return;
      var i = 0;
      items.forEach(function (el, idx) { el.hidden = idx !== 0; });
      var interval = parseInt(bar.getAttribute('data-announcement-rotate'), 10) || 5;
      setInterval(function () {
        items[i].hidden = true;
        i = (i + 1) % items.length;
        items[i].hidden = false;
      }, interval * 1000);
    });
  }

  /* ------------------------------------------------------------------ *
   * Collection facets (filter/sort) — uses Shopify's native filtering
   * ------------------------------------------------------------------ */
  function initFacets() {
    var root = $('[data-facets]');
    if (!root) return;

    function render(url, pushState) {
      var grid = $('[data-product-grid-container]');
      if (grid) grid.classList.add('is-busy');
      fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + root.getAttribute('data-section-id'))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          ['[data-product-grid-container]', '[data-active-facets]', '[data-facets-count]'].forEach(function (sel) {
            var incoming = doc.querySelector(sel);
            var target = document.querySelector(sel);
            if (incoming && target) target.innerHTML = incoming.innerHTML;
          });
          // Keep every filter form in sync (sidebar on desktop, drawer on mobile)
          var incomingForms = doc.querySelectorAll('[data-facets-form]');
          $$('[data-facets-form]').forEach(function (form, i) {
            if (incomingForms[i]) form.innerHTML = incomingForms[i].innerHTML;
          });
          if (pushState) window.history.pushState({ url: url }, '', url);
          if (grid) grid.classList.remove('is-busy');
          initReveal();
        })
        .catch(function () { window.location.href = url; });
    }

    function formUrl(form) {
      var params = new URLSearchParams(new FormData(form));
      // Strip empty values so URLs stay clean
      var clean = new URLSearchParams();
      params.forEach(function (value, key) { if (value) clean.append(key, value); });
      var qs = clean.toString();
      return window.location.pathname + (qs ? '?' + qs : '');
    }

    document.addEventListener('change', function (e) {
      var form = e.target.closest('[data-facets-form]');
      if (!form) return;
      render(formUrl(form), true);
    });

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-facet-remove], [data-facets] .pagination a');
      if (!link) return;
      e.preventDefault();
      render(link.getAttribute('href'), true);
      if (link.closest('.pagination')) window.scrollTo({ top: root.offsetTop - 90, behavior: PREFERS_REDUCED ? 'auto' : 'smooth' });
    });

    document.addEventListener('change', function (e) {
      var sort = e.target.closest('[data-sort-by]');
      if (!sort) return;
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      render(url.pathname + url.search, true);
    });

    on(window, 'popstate', function () { render(window.location.pathname + window.location.search, false); });
  }

  /* ------------------------------------------------------------------ *
   * Copy product link
   * ------------------------------------------------------------------ */
  function initCopyLink() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-copy-link]');
      if (!link || !navigator.clipboard) return;
      e.preventDefault();
      navigator.clipboard.writeText(link.href).then(function () {
        Toast.show(strings.linkCopied || 'Link copied');
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Newsletter & contact form anchors (scroll to success/error message)
   * ------------------------------------------------------------------ */
  function initFormAnchors() {
    if (window.location.hash && /contact_form|newsletter/.test(window.location.hash)) {
      var el = document.querySelector(window.location.hash);
      if (el) setTimeout(function () { el.scrollIntoView({ behavior: PREFERS_REDUCED ? 'auto' : 'smooth', block: 'center' }); }, 200);
    }
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function init(ctx) {
    initReveal();
    initCarousels(ctx);
    initVariantPickers(ctx);
    initGallery(ctx);
  }

  function boot() {
    document.documentElement.classList.remove('no-js');
    initHeader();
    initNav();
    initSearch();
    initCart();
    initQuickAdd();
    initParallax();
    initAnnouncement();
    initFacets();
    initCopyLink();
    initFormAnchors();
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify Theme Editor lifecycle
  document.addEventListener('shopify:section:load', function (e) {
    initNav();
    initFacets();
    initParallax();
    initAnnouncement();
    init(e.target);
  });
  document.addEventListener('shopify:section:select', function (e) {
    var overlayish = e.target.querySelector('[data-editor-open]');
    if (overlayish) openOverlay(overlayish);
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var overlayish = e.target.querySelector('[data-editor-open]');
    if (overlayish) closeOverlay(overlayish);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target.closest('.carousel__slide');
    if (slide && slide.parentElement) {
      slide.parentElement.scrollTo({ left: slide.offsetLeft - slide.parentElement.offsetLeft, behavior: 'smooth' });
    }
  });
})();
